import { useMemo } from "react";
import { Task, TaskInstance, InstanceStatus, RecurrencePattern } from "@/lib/api/checklist";
import { getPeriodsForRecurrence, groupInstancesByPeriod, getDateRangeForRecurrence } from "@/lib/utils/recurrence";
import { Check, Clock, AlertCircle, Calendar } from "lucide-react";
import { cn } from "@/lib/utils";
import moment from "moment-timezone";
import { CommonDestinationTimezone } from "@/lib/utils";
import { getYear } from "date-fns";

interface QuarterlyBreakdownViewProps {
  tasks: Task[];
  instances: TaskInstance[];
  lookbackQuarters: number;
  isFiscalYear?: boolean;
}

interface QuarterData {
  year: number;
  quarter: number;
  label: string;
  periodKey: string;
}

export function QuarterlyBreakdownView({ tasks, instances, lookbackQuarters, isFiscalYear = false }: QuarterlyBreakdownViewProps) {
  // Get date range and periods
  const { start, end } = getDateRangeForRecurrence(RecurrencePattern.QUARTERLY, lookbackQuarters);
  const periods = getPeriodsForRecurrence(RecurrencePattern.QUARTERLY, start, end);
  
  // Group periods by year for display
  const quartersByYear = useMemo(() => {
    const grouped = new Map<number, QuarterData[]>();
    
    periods.forEach(period => {
      const periodDate = period.start;
      let displayYear = getYear(periodDate);
      const quarter = parseInt(period.label.replace('Q', ''));
      
      // For fiscal year (July-June):
      // Calendar Q3 (Jul-Sep) and Q4 (Oct-Dec) belong to the next fiscal year
      // Calendar Q1 (Jan-Mar) and Q2 (Apr-Jun) belong to the current fiscal year
      if (isFiscalYear) {
        if (quarter >= 3) {
          // Q3 (Jul-Sep) and Q4 (Oct-Dec) belong to the fiscal year ending the following June
          displayYear = displayYear + 1;
        }
        // Q1 (Jan-Mar) and Q2 (Apr-Jun) stay with their calendar year for fiscal year
      }
      
      const quarterData: QuarterData = {
        year: displayYear,
        quarter,
        label: period.label,
        periodKey: period.key,
      };
      
      const existing = grouped.get(displayYear) || [];
      grouped.set(displayYear, [...existing, quarterData]);
    });
    
    return Array.from(grouped.entries())
      .sort((a, b) => b[0] - a[0]) // Sort years descending (newest first)
      .map(([year, quarters]) => ({
        year,
        quarters: quarters.sort((a, b) => {
          // Always show Q4 -> Q3 -> Q2 -> Q1 order
          return b.quarter - a.quarter;
        }),
      }));
  }, [periods, isFiscalYear]);
  
  // Group instances by task and period
  const instancesByTaskAndPeriod = useMemo(() => {
    const grouped = new Map<string, Map<string, TaskInstance[]>>();
    
    tasks.forEach(task => {
      const taskInstances = instances.filter(inst => inst.task_id === task.id);
      const periodMap = groupInstancesByPeriod(taskInstances, periods);
      grouped.set(task.id, periodMap);
    });
    
    return grouped;
  }, [tasks, instances, periods]);
  
  // Get task deadline from first instance
  const getTaskDeadline = (task: Task): string => {
    const taskInstances = instances.filter(inst => inst.task_id === task.id);
    if (taskInstances.length > 0) {
      const firstInstance = taskInstances.sort((a, b) => 
        new Date(a.due_date).getTime() - new Date(b.due_date).getTime()
      )[0];
      return moment.tz(firstInstance.due_date, CommonDestinationTimezone).format("MMMM Do");
    }
    return "";
  };
  
  // Get status for a task's quarter
  const getQuarterStatus = (task: Task, periodKey: string) => {
    const taskPeriodMap = instancesByTaskAndPeriod.get(task.id);
    const periodInstances = taskPeriodMap?.get(periodKey) || [];
    
    if (periodInstances.length === 0) {
      const period = periods.find(p => p.key === periodKey);
      if (period && period.start > new Date()) {
        return { status: 'future', label: '', icon: null };
      }
      return { status: 'no-data', label: '', icon: null };
    }
    
    const completed = periodInstances.find(inst => inst.status === InstanceStatus.COMPLETED);
    const pending = periodInstances.find(inst => inst.status === InstanceStatus.PENDING);
    
    if (completed) {
      return {
        status: completed.is_late ? 'completed-late' : 'completed',
        label: completed.is_late ? 'Done Late' : 'Done',
        icon: <Check className="h-3 w-3" />,
      };
    }
    
    if (pending) {
      return {
        status: pending.is_late ? 'late' : 'pending',
        label: pending.is_late ? 'Late' : 'Pending',
        icon: pending.is_late ? <AlertCircle className="h-3 w-3" /> : <Clock className="h-3 w-3" />,
      };
    }
    
    return { status: 'unknown', label: '', icon: null };
  };
  
  // All years represented in the data
  const allYears = quartersByYear.map(({ year }) => year);
  
  // Sort tasks: those with data first, then those without
  const sortedTasks = useMemo(() => {
    return [...tasks].sort((a, b) => {
      const aInstances = instances.filter(inst => inst.task_id === a.id);
      const bInstances = instances.filter(inst => inst.task_id === b.id);
      
      // Tasks with instances come first
      if (aInstances.length > 0 && bInstances.length === 0) return -1;
      if (aInstances.length === 0 && bInstances.length > 0) return 1;
      
      // If both have instances or both don't, maintain original order
      return 0;
    });
  }, [tasks, instances]);
  
  return (
    <div className="space-y-1">
      {/* Header with year labels */}
      <div className="flex gap-1 mb-2">
        <div className="w-[280px]" /> {/* Space for task names */}
        {allYears.map(year => {
          const yearData = quartersByYear.find(yd => yd.year === year);
          // Always show quarters in Q4 -> Q1 order
          // In fiscal year view, we need to map calendar quarters to fiscal quarters
          const quarterLabels = ['Q4', 'Q3', 'Q2', 'Q1'];
          
          return (
            <div key={year} className="flex-1">
              <div className="text-center">
                <div className="text-sm font-medium text-muted-foreground">
                  <b>{isFiscalYear ? `FY${year}` : year}</b>
                </div>
                <div className="flex gap-1 mt-1">
                  {quarterLabels.map((displayQ) => {
                    // In fiscal year view, map display quarters to actual calendar quarters
                    let calendarQ = displayQ;
                    if (isFiscalYear) {
                      // Map fiscal quarters to calendar quarters:
                      // Fiscal Q1 (Jul-Sep) = Calendar Q3
                      // Fiscal Q2 (Oct-Dec) = Calendar Q4
                      // Fiscal Q3 (Jan-Mar) = Calendar Q1
                      // Fiscal Q4 (Apr-Jun) = Calendar Q2
                      switch(displayQ) {
                        case 'Q1': calendarQ = 'Q3'; break;
                        case 'Q2': calendarQ = 'Q4'; break;
                        case 'Q3': calendarQ = 'Q1'; break;
                        case 'Q4': calendarQ = 'Q2'; break;
                      }
                    }
                    
                    // Check if this quarter exists in our data range using calendar quarter
                    const quarterExists = yearData?.quarters.some(quarter => quarter.label === calendarQ);
                    
                    // Add month ranges based on display quarter
                    let monthRange = '';
                    switch(displayQ) {
                      case 'Q1': monthRange = isFiscalYear ? 'Jul-Sep' : 'Jan-Mar'; break;
                      case 'Q2': monthRange = isFiscalYear ? 'Oct-Dec' : 'Apr-Jun'; break;
                      case 'Q3': monthRange = isFiscalYear ? 'Jan-Mar' : 'Jul-Sep'; break;
                      case 'Q4': monthRange = isFiscalYear ? 'Apr-Jun' : 'Oct-Dec'; break;
                    }
                    
                    return (
                      <div 
                        key={displayQ} 
                        className={cn(
                          "flex-1 text-xs text-center rounded px-1",
                          quarterExists 
                            ? "bg-yellow-100 text-yellow-800 font-medium" 
                            : "text-muted-foreground"
                        )}
                      >
                        <div>{displayQ}</div>
                        <div className="text-[10px] opacity-80">{monthRange}</div>
                      </div>
                    )
                  })}
                </div>
              </div>
              {isFiscalYear && (
                <div 
                  className={cn(
                    "h-1 mt-1 rounded-full",
                    // Alternate colors for fiscal years
                    (allYears.indexOf(year) % 2 === 0) 
                      ? "bg-purple-400" 
                      : "bg-teal-400"
                  )}
                />
              )}
            </div>
          );
        })}
      </div>
      
      {/* Tasks */}
      {sortedTasks.map(task => {
        const deadline = getTaskDeadline(task);

        return (
          <div key={task.id} className="flex gap-1 items-center group hover:bg-muted/50 rounded-lg p-1 -m-1">
            {/* Task Info */}
            <div className="w-[280px] pr-2">
              <div className="text-sm font-medium truncate">{task.title}</div>
              {deadline && (
                <div className="text-xs text-muted-foreground flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {deadline}
                </div>
              )}
            </div>
            
            {/* Quarters by Year */}
            {allYears.map(year => {
              const yearData = quartersByYear.find(yd => yd.year === year);
              // Always show quarters in Q4 -> Q1 order
              const quarterLabels = ['Q4', 'Q3', 'Q2', 'Q1'];
              
              return (
                <div key={year} className="flex-1 flex gap-1">
                  {quarterLabels.map(displayQ => {
                    // In fiscal year view, map display quarters to actual calendar quarters
                    let calendarQ = displayQ;
                    if (isFiscalYear) {
                      switch(displayQ) {
                        case 'Q1': calendarQ = 'Q3'; break;
                        case 'Q2': calendarQ = 'Q4'; break;
                        case 'Q3': calendarQ = 'Q1'; break;
                        case 'Q4': calendarQ = 'Q2'; break;
                      }
                    }
                    
                    const quarter = yearData?.quarters.find(q => q.label === calendarQ);
                    
                    if (!quarter) {
                      // This year doesn't have this quarter in our date range
                      return (
                        <div 
                          key={displayQ} 
                          className="flex-1 min-w-[40px] rounded px-1 py-0.5 flex items-center justify-center"
                        >
                          <div className="w-4 h-px bg-gray-300" />
                        </div>
                      );
                    }
                    
                    const { status, label, icon } = getQuarterStatus(task, quarter.periodKey);
                    const period = periods.find(p => p.key === quarter.periodKey);
                    const isFuture = period && period.start > new Date();
                    const hasData = status !== 'no-data' && status !== 'future';
                    
                    return (
                      <div
                        key={quarter.periodKey}
                        className={cn(
                          "flex-1 min-w-[40px] rounded px-1 py-0.5 text-center transition-all",
                          "flex items-center justify-center gap-0.5",
                          "border-2",
                          // Border styles
                          hasData && !isFuture && "border-indigo-400",
                          !hasData && !isFuture && "border-transparent",
                          isFuture && "border-transparent opacity-50",
                          // Background styles
                          status === 'completed' && "bg-green-100 text-green-700",
                          status === 'completed-late' && "bg-orange-100 text-orange-700",
                          status === 'pending' && "bg-blue-100 text-blue-700",
                          status === 'late' && "bg-red-100 text-red-700",
                          status === 'no-data' && !isFuture && "border-gray-500 py-0.5",
                          isFuture && "bg-gray-100 text-gray-400"
                        )}
                      >
                        {icon}
                        {label && <span className="text-[10px] font-medium">{label}</span>}
                        {(!label && status === 'no-data') && <span className="text-[10px] font-medium">No Data</span>}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        );
      })}
      
      {/* Compact Legend */}
      <div className="flex items-center gap-3 text-[10px] text-muted-foreground mt-4 pt-2 border-t">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-green-100" />
          <span>Completed</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-orange-100" />
          <span>Completed Late</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-blue-100" />
          <span>Pending</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-red-100" />
          <span>Late</span>
        </div>
      </div>
    </div>
  );
}
